import {
  CONTRACT_TYPE_AUTO_APPROVAL_COUNTED_CASES,
  CONTRACT_TYPE_AUTO_APPROVAL_FIGURES,
  type ContractTypeAutoApprovalBasis,
  type ContractTypeAutoApprovalResponse,
  type ContractTypeGrain,
} from '@clm/shared-types';

import { ApiError } from '../../api/errors';
import { classifyApiError } from '../../api/problem';
import { refusalCodeOf } from '../base-msa-shared';
import { AUTO_APPROVAL_PAUSE_COPY, AUTO_APPROVAL_STATE_COPY } from '../type-proposal-copy';
import { TOO_FEW_DECISIONS_COPY, type PolicyProblem } from './shared';

/**
 * The pure half of the Auto-approval page's **Contract type** card — CLM-121
 * (D-110-4 points 3 and 6; R-121-13 … R-121-16). Kept out of the component
 * (the `shared.ts` split): the copy and the refusal words have tests of their
 * own, and fast refresh stays whole.
 *
 * Every figure is the API's: the card never recomputes the state, the bar or
 * the sample. The fixed figures are printed as fixed — they are D-110-4's,
 * never settings (Gerry, 1943297: no Legal co-sign for this switch).
 */

const FIGURES = CONTRACT_TYPE_AUTO_APPROVAL_FIGURES;

/** The four numbers, as fixed — never settings. */
export const TYPE_SWITCH_FIXED_FIGURES_COPY =
  `Above ${FIGURES.threshold} over at least ${String(FIGURES.minimum_sample)} counted decisions · 1 in 5 sampled · ` +
  `paused after ${String(FIGURES.stale_after_days)} days without a counted check — fixed (D-110-4), not settings`;

/** What the switch does, and who may use it. */
export const TYPE_SWITCH_INTRO_COPY =
  'When on, an MSA or NDA the AI decides for a contract whose file name gave no type is approved by policy, with no ' +
  'administrator step — never a SOW, OTHER, a conflicting name, a possible amendment or a vendor’s contract. An ' +
  'administrator with Scope-All switches it on or off alone, with no Legal co-sign. A type already approved stays ' +
  'approved when the switch goes off, pauses or lapses.';

const [NO_NAME_TYPE_CASE, UNKNOWN_EVIDENCE_CASE] = CONTRACT_TYPE_AUTO_APPROVAL_COUNTED_CASES;

/**
 * The counted cases, named — what 6b counts. The second counts only on a person's
 * type, the AI's or a placeholder `OTHER` (`CONTRACT_TYPE_AUTO_APPROVAL_UNKNOWN_EVIDENCE_SOURCES`):
 * a legacy or file-name MSA, NDA or SOW with no evidence is left out.
 */
export const COUNTED_CASES_COPY =
  `${NO_NAME_TYPE_CASE}, and ${UNKNOWN_EVIDENCE_CASE} on a person’s type, the AI’s or a placeholder OTHER: ` +
  'the file name gave no type';

/** Said beside "AI against the file name": shown, never used. */
export const AGAINST_NAME_DISPLAY_ONLY_COPY = 'shown only; never read by a rule';

/** Why switching on needs a note. */
export const SWITCH_ON_NOTE_COPY =
  'Switching on needs a reason: it lets AI-decided MSAs and NDAs skip the administrator, and the reason is recorded with the revision.';

/** The grain, as one line. */
export function typeGrainCopy(grain: ContractTypeGrain | null): string {
  return grain === null ? 'never read' : `${grain.extraction_schema_version} · ${grain.extraction_model}`;
}

/** The rate as the API rendered it, or "too few decisions" below 30 — never a number (CLM-107's rule). */
export function typeRateCopy(basis: ContractTypeAutoApprovalBasis): string {
  return basis.sample_size < FIGURES.minimum_sample || basis.accuracy_rate === null ? TOO_FEW_DECISIONS_COPY : basis.accuracy_rate;
}

/** `28 / 2, n = 30, rate 0.933`. */
export function typeBasisLine(basis: ContractTypeAutoApprovalBasis): string {
  return `${String(basis.verified_count)} / ${String(basis.corrected_count)}, n = ${String(basis.sample_size)}, rate ${typeRateCopy(basis)}`;
}

/** `2026-10-07`, from the API's instant. */
function day(utc: string): string {
  return utc.slice(0, 10);
}

/**
 * The state in one sentence, with the cause where the API names it: who paused
 * it and on what, the reading that lapsed it, or since when it is stale.
 */
export function typeStateSentence(response: ContractTypeAutoApprovalResponse): string | null {
  const { state, pause, lapse } = response;
  if (state === null) return null;
  if (state === 'lapsed' && lapse !== null) {
    const was = response.switch.current?.grain ?? null;
    if (was !== null) {
      return (
        `Lapsed — the switch was turned on for schema ${was.extraction_schema_version} / model ${was.extraction_model}; ` +
        `on ${day(lapse.since_utc)} a reading came from schema ${lapse.extraction_schema_version} / model ` +
        `${lapse.extraction_model}. It stays lapsed, even if readings return to ${was.extraction_schema_version} / ` +
        `${was.extraction_model}. Switch it on again once the figure qualifies.`
      );
    }
  }
  if (state === 'paused_by_correction' && pause !== null) {
    return (
      `Paused — ${pause.decided_by_upn ?? 'an administrator'} corrected ${pause.contract_key} ` +
      `(${AUTO_APPROVAL_PAUSE_COPY[pause.kind]}) on ${day(pause.decided_utc)}; auto-approval stays paused until an ` +
      'administrator switches it on again.'
    );
  }
  const last = response.basis?.last_decision_utc ?? null;
  if (state === 'stale' && last !== null) {
    return `Paused — no counted check since ${day(last)}; it resumes on the next one.`;
  }
  return AUTO_APPROVAL_STATE_COPY[state];
}

type Copy = { readonly head: string; readonly body: string; readonly reload?: boolean };

const VALIDATION_COPY: Readonly<Record<string, Copy>> = Object.freeze({
  note_required: { head: 'A note is required', body: SWITCH_ON_NOTE_COPY },
  no_change: { head: 'Nothing to save', body: 'The switch is already in that state. Nothing was recorded.' },
});

const CONFLICT_COPY: Readonly<Record<string, Copy>> = Object.freeze({
  policy_changed: {
    head: 'The switch changed since this page loaded',
    body: 'Another change was saved first, so nothing was recorded. The page has been reloaded — check it, then decide again.',
    reload: true,
  },
  no_reading_grain: {
    head: 'No figure to switch on at',
    body: 'The AI has not read a contract type yet, so there is no model and schema version to take a figure at.',
  },
  too_few_decisions: {
    head: 'Too few decisions',
    body: `Switching on needs at least ${String(FIGURES.minimum_sample)} counted decisions at the current model and schema version (D-110-4).`,
    reload: true,
  },
  not_above_threshold: {
    head: 'Not above 0.900',
    body: 'The counted accuracy at the current model and schema version is 0.900 or below; switching on needs it strictly above (D-110-3).',
    reload: true,
  },
});

/** A failed switch, in the page's words; a code is trusted only with the status the API sends it under. */
export function typeSwitchProblemOf(error: unknown): PolicyProblem {
  const kind = classifyApiError(error);
  const code = refusalCodeOf(error);
  const correlation_id = error instanceof ApiError ? error.correlation_id : null;
  const table = kind === 'validation' ? VALIDATION_COPY : kind === 'conflict' ? CONFLICT_COPY : null;
  const found = table === null || code === null ? undefined : table[code];
  if (found === undefined || code === null) return { kind, correlation_id, refusal: null };
  return {
    kind,
    correlation_id,
    refusal: { code, tone: kind === 'conflict' ? 'up' : 'bad', head: found.head, body: found.body, reload: found.reload === true },
  };
}
